import React from "react";
import styled from "styled-components";

import { useIntl } from "../../../../../shared/core/i18n/use-intl";
import type { AccountingTransaction } from "../../../../../shared/domains/accounting-transaction/transaction/transaction";
import { useTransactionLinks } from "../../../../../shared/domains/accounting-transaction/use-transaction-links";
import { getTotalOfDetailedAccounting } from "../../../../../shared/utils/total-amount";
import { AmountText } from "../../../common/amount-text";
import { UnstyledButton } from "../../../common/buttons/default-button";
import { ChevronIcon } from "../../../common/svg/chevron";
import { theme } from "../../../styles/theme";
import { hasObjectType } from "./account-transaction-card";
import { TransactionStateTag } from "./transaction-state-tag";

interface TransactionRowProps {
  transaction: AccountingTransaction;
  className?: string;
  onClick: () => void;
}

/** One operation of a list: what it was, when, where it stands, and how much. */
export function TransactionRow({ transaction, className, onClick }: Readonly<TransactionRowProps>) {
  const { formatMessage, formatDate } = useIntl();
  const { isOutgoing } = useTransactionLinks();

  return (
    <Row className={className} onClick={onClick}>
      <RowContent>
        <Label>{describe(transaction)}</Label>
        <Date>{formatDate(transaction.date, { day: "2-digit", month: "2-digit", year: "numeric" })}</Date>
        {/* Justification and qualification only apply to expenses, never to incoming money. */}
        {isOutgoing(transaction) && (
          <TagRow>
            <TransactionStateTag
              label={formatMessage(
                transaction.justified ? "accountTransactions.tagJustified" : "accountTransactions.tagNotJustified",
              )}
              done={!!transaction.justified}
            />
            <TransactionStateTag
              label={formatMessage(
                transaction.qualified ? "accountTransactions.tagQualified" : "accountTransactions.tagNotQualified",
              )}
              done={!!transaction.qualified}
            />
          </TagRow>
        )}
      </RowContent>
      {transaction.detailedAccounting && transaction.amount && (
        <RowAmount
          amount={{
            value: getTotalOfDetailedAccounting(transaction.detailedAccounting),
            currency: transaction.amount.currency,
          }}
        />
      )}
      <RowChevron />
    </Row>
  );
}

function describe(transaction: AccountingTransaction) {
  return hasObjectType(transaction) ? transaction.shortDescription || transaction.type.label : "";
}

const Row = styled(UnstyledButton)`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  width: 100%;
  margin-bottom: 12px;
  padding: 16px;
  border: 1px solid ${theme.colors.gray[100]};
  border-radius: 12px;
  background-color: #ffffff;
  text-align: left;

  &:hover {
    opacity: 1;
    border-color: #e4e4e4;
  }
`;

const RowContent = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
`;

const Label = styled.span`
  ${theme.boldText};
  font-size: 15px;
  line-height: 20px;
  color: #000000;
`;

const Date = styled.span`
  font-size: 13px;
  line-height: 17px;
  color: ${theme.colors.gray[500]};
`;

const TagRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 4px;
`;

const RowAmount = styled(AmountText)`
  ${theme.boldText};
  font-size: 15px;
  color: #000000;
`;

const RowChevron = styled(ChevronIcon)`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;
